import { useEffect, useMemo, useState } from 'react';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';
import {
  MdRotateLeft,
  MdRotateRight,
  MdRefresh,
  MdZoomIn,
  MdRotate90DegreesCcw,
} from 'react-icons/md';
import { Modal } from '../Modal';
import { Button } from '../Button';
import { getCroppedImage } from './getCroppedImage';

type AspectMode = 'original' | 'square' | 'landscape' | 'portrait';

const ASPECT_RATIOS: Record<Exclude<AspectMode, 'original'>, number> = {
  square: 1,
  landscape: 4 / 3,
  portrait: 3 / 4,
};

const ASPECT_OPTIONS: { mode: AspectMode; label: string }[] = [
  { mode: 'original', label: 'Original' },
  { mode: 'square', label: '1:1' },
  { mode: 'landscape', label: '4:3' },
  { mode: 'portrait', label: '3:4' },
];

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

export function ImageEditorModal({
  isOpen,
  file,
  title = 'Edit image',
  lockedAspect,
  onCancel,
  onApply,
}: {
  isOpen: boolean;
  file: File | null;
  title?: string;
  lockedAspect?: number;
  onCancel: () => void;
  onApply: (editedFile: File) => void;
}) {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [rotation, setRotation] = useState(0);
  const [aspectMode, setAspectMode] = useState<AspectMode>('original');
  const [naturalAspect, setNaturalAspect] = useState<number | null>(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (!file) {
      setImageSrc(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setImageSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(MIN_ZOOM);
    setRotation(0);
    setAspectMode('original');
    setNaturalAspect(null);
    setCroppedAreaPixels(null);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const aspect = useMemo(() => {
    if (lockedAspect) return lockedAspect;
    if (aspectMode === 'original') return naturalAspect ?? 1;
    return ASPECT_RATIOS[aspectMode];
  }, [lockedAspect, aspectMode, naturalAspect]);

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(MIN_ZOOM);
    setRotation(0);
    setAspectMode('original');
  };

  const rotateBy = (degrees: number) => {
    setRotation((current) => {
      const next = (current + degrees) % 360;
      return next < 0 ? next + 360 : next;
    });
  };

  const handleApply = async () => {
    if (!file || !imageSrc || !croppedAreaPixels) return;
    setIsProcessing(true);
    try {
      const editedFile = await getCroppedImage(
        imageSrc,
        croppedAreaPixels,
        rotation,
        file.type,
        file.name
      );
      onApply(editedFile);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={(value) => {
        if (!value) onCancel();
      }}
      title={title}
    >
      <div className='flex w-[min(88vw,44rem)] flex-col space-y-4'>
        <div className='relative h-[48vh] min-h-[300px] w-full overflow-hidden rounded-lg bg-gray-900'>
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect}
              minZoom={MIN_ZOOM}
              maxZoom={MAX_ZOOM}
              showGrid
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={(_, areaPixels) => setCroppedAreaPixels(areaPixels)}
              onMediaLoaded={(mediaSize) =>
                setNaturalAspect(mediaSize.naturalWidth / mediaSize.naturalHeight)
              }
            />
          )}
        </div>

        <div className='flex flex-wrap items-center gap-2'>
          {!lockedAspect && (
            <>
              <span className='mr-1 text-sm font-medium text-gray-700 font-inter'>
                Aspect
              </span>
              {ASPECT_OPTIONS.map((option) => {
                const isSelected = aspectMode === option.mode;
                return (
                  <button
                    key={option.mode}
                    type='button'
                    onClick={() => setAspectMode(option.mode)}
                    className={`rounded-md border px-3 py-1 text-sm font-inter transition-colors ${
                      isSelected
                        ? 'border-primary bg-blue-50 text-primary'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </>
          )}
          <div className='ml-auto flex items-center gap-1'>
            <button
              type='button'
              onClick={() => rotateBy(-90)}
              aria-label='Rotate left 90 degrees'
              className='flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50'
            >
              <MdRotateLeft size={20} />
            </button>
            <button
              type='button'
              onClick={() => rotateBy(90)}
              aria-label='Rotate right 90 degrees'
              className='flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50'
            >
              <MdRotateRight size={20} />
            </button>
            <button
              type='button'
              onClick={handleReset}
              aria-label='Reset edits'
              className='flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50'
            >
              <MdRefresh size={20} />
            </button>
          </div>
        </div>

        <label className='flex items-center gap-3'>
          <MdZoomIn size={20} className='shrink-0 text-gray-500' />
          <input
            type='range'
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.01}
            value={zoom}
            onChange={(event) => setZoom(Number(event.target.value))}
            aria-label='Zoom'
            className='w-full cursor-pointer accent-primary'
          />
        </label>

        <label className='flex items-center gap-3'>
          <MdRotate90DegreesCcw size={20} className='shrink-0 text-gray-500' />
          <input
            type='range'
            min={0}
            max={360}
            step={1}
            value={rotation}
            onChange={(event) => setRotation(Number(event.target.value))}
            aria-label='Rotation'
            className='w-full cursor-pointer accent-primary'
          />
        </label>

        <div className='flex justify-end gap-3 border-t border-gray-200 pt-4'>
          <Button
            label='Cancel'
            intent='secondary'
            size='sm'
            onClick={onCancel}
            disabled={isProcessing}
          />
          <Button
            label={isProcessing ? 'Applying…' : 'Apply'}
            intent='primary'
            size='sm'
            onClick={handleApply}
            disabled={isProcessing || !croppedAreaPixels}
          />
        </div>
      </div>
    </Modal>
  );
}
